import { ChevronRight, X } from 'lucide-react'

import { useSignedUrl } from '@/hooks/useSignedUrl'
import type { NextPreviewStage } from '@/lib/next-episode-timing'

import { useDragOffset } from '../hooks/useDragOffset'

type NextEpisodePreviewProps = {
  /** null のときは表示しない。chip は控えめなボタン、card はサムネイル付き */
  stage: NextPreviewStage | null
  nextEpisodeId?: string
  nextEpisodeTitle?: string
  controlsVisible?: boolean
  currentTime: number
  duration: number
  onNext?: () => void
  onDismiss: () => void
  isLandscape?: boolean
}

export const NextEpisodePreview = ({
  stage,
  nextEpisodeId,
  nextEpisodeTitle,
  controlsVisible = false,
  currentTime,
  duration,
  onNext,
  onDismiss,
  isLandscape = false,
}: NextEpisodePreviewProps) => {
  const thumbnailPath = nextEpisodeId && stage === 'card' ? `/api/e/${nextEpisodeId}/thumbnail` : null
  const { url: thumbnailUrl } = useSignedUrl(thumbnailPath)

  // 向きごとに位置を保持（回転時に画面外へ出ないよう別管理）
  const landscapeDrag = useDragOffset()
  const portraitDrag = useDragOffset()
  const { attach: attachDrag, handlers: dragHandlers, offset: dragOffset } = isLandscape ? landscapeDrag : portraitDrag
  const dragTranslate = `${dragOffset.x}px ${dragOffset.y}px`

  const remainingTime = Math.max(0, duration - currentTime)

  if (!stage || !nextEpisodeId || !onNext) {
    return null
  }

  if (stage === 'chip') {
    return (
      <div
        className="fixed right-4 z-9999 animate-in slide-in-from-bottom-2 fade-in duration-300 transition-[bottom] ease-out"
        style={{
          bottom: isLandscape ? (controlsVisible ? '5rem' : '1rem') : controlsVisible ? '8rem' : '5rem',
        }}
      >
        <button
          onClick={onNext}
          className="flex items-center gap-1 bg-zinc-900/80 text-white text-xs font-medium rounded-full border border-white/10 shadow-lg cursor-pointer hover:bg-zinc-800/90 transition-colors px-3 py-1.5"
        >
          次の動画
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    )
  }

  if (isLandscape) {
    // Landscape mode: small preview in bottom-right
    return (
      <div
        ref={attachDrag}
        {...dragHandlers}
        className="fixed right-4 z-9999 animate-in slide-in-from-bottom-2 fade-in duration-300 transition-[bottom] ease-out touch-none select-none cursor-grab active:cursor-grabbing"
        style={{ bottom: controlsVisible ? '5rem' : '1rem', translate: dragTranslate }}
      >
        <button
          onClick={onNext}
          className="flex flex-col bg-zinc-900/95 rounded-lg overflow-hidden shadow-2xl border border-white/10 cursor-pointer hover:bg-zinc-800/95 transition-colors w-50 p-0!"
        >
          {/* サムネイル */}
          {thumbnailUrl && (
            <div className="relative w-full h-28 shrink-0">
              <img src={thumbnailUrl} alt="" draggable={false} className="w-full h-full object-cover" />
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  onDismiss()
                }}
                className="absolute top-1 right-1 bg-black/50 rounded-full p-2 text-white hover:bg-black/70 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          )}

          {/* テキスト */}
          <div className="px-2.5 py-2 text-left">
            <div className="flex items-center gap-2">
              <p className="text-gray-400 text-[10px] font-medium">次の動画</p>
              <p className="text-gray-300 text-[10px]">
                <span className="font-bold text-white">{Math.ceil(remainingTime)}</span>秒後
              </p>
            </div>
            {nextEpisodeTitle && (
              <p className="text-white text-[10px] font-medium truncate mt-0.5">{nextEpisodeTitle}</p>
            )}
          </div>
        </button>
      </div>
    )
  }

  // Portrait mode: wide preview at bottom
  return (
    <div
      ref={attachDrag}
      {...dragHandlers}
      className="fixed left-[5%] right-[2%] z-9999 animate-in slide-in-from-bottom-2 fade-in duration-300 transition-[bottom] ease-out touch-none select-none cursor-grab active:cursor-grabbing"
      style={{ bottom: controlsVisible ? '8rem' : '5em', translate: dragTranslate }}
    >
      <button
        onClick={onNext}
        className="flex items-stretch bg-zinc-900/95 rounded-lg overflow-hidden shadow-2xl border border-white/10 cursor-pointer hover:bg-zinc-800/95 transition-colors w-full p-0!"
      >
        {/* サムネイル */}
        {thumbnailUrl && (
          <div className="relative w-[30%] shrink-0">
            <img src={thumbnailUrl} alt="" draggable={false} className="w-full h-full object-cover" />
          </div>
        )}

        {/* テキスト */}
        <div className="flex-1 px-2.5 text-left min-w-0">
          <div className="flex items-center justify-between gap-1 py-2">
            <p className="text-gray-400 text-[10px] font-medium">次の動画</p>
            <button
              onClick={(e) => {
                e.stopPropagation()
                onDismiss()
              }}
              className="text-gray-400 hover:text-white transition-colors shrink-0 p-1!"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
          <p className="text-gray-300 text-[11px] mt-0.5">
            あと <span className="font-bold text-white">{Math.ceil(remainingTime)}</span> 秒で開始します
          </p>
          {nextEpisodeTitle && <p className="text-white text-xs font-medium truncate mt-0.5">{nextEpisodeTitle}</p>}
        </div>
      </button>
    </div>
  )
}
